import { useChat, type UIMessage } from '@ai-sdk/react'
import { lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { DefaultChatTransport } from 'ai'
import takaIcon from 'assets/taka-logo-icon.svg'
import { useAnalytics, useIsMobile } from 'hooks'
import { ComposerRow, ComposerSendButton } from 'components'
import { AlertTriangle, Loader2, RefreshCw, X } from 'lucide-react'
import { ComposerCreditBlock, selectIsOutOfCredits, useSubscriptionGate } from 'modules/billing'
import { useAppSelector } from 'modules/core'
import { OpenAPI } from 'openapi'
import { request } from 'openapi/core/request'
import { OpenAPI as OpenAPIConfig } from 'openapi/core/OpenAPI'
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ulid } from 'ulid'
import {
  BRAND_TOOL_ACTIVITY_EVENT,
  BRAND_TOOL_TYPES,
  SCHEDULE_TOOL_ACTIVITY_EVENT,
  SCHEDULE_TOOL_TYPES,
} from '../assistant-chat-events'
import { useGenerationStatusStore } from '../store/generation-status-store'
import { useIdeaStore } from '../store/idea-store'
import type { Creative } from '../types'
import { creativeTypeMeta } from '../types'
import { buildEmptyResponseDiagnostics } from '../utils/empty-response-diagnostics'
import { ChatMessageBubble, ThinkingBubble } from './SideChat'
import { CreativeGenerationCard } from './CreativeGenerationCard'
import { API_BASE, type Part, parseOptions, partsText } from '../utils/chat-markers'

const FALLBACK_PROMPTS = [
  'Create an Instagram post about my latest offer',
  'Make a TikTok video for my business',
  'Write a LinkedIn post sharing industry insights',
  'Plan a week of content for my brand',
  'Schedule my next 7 days of posts',
]

/** Derive "Try asking" prompts from the user's suggestion creatives */
function buildQuickPrompts(suggestions: Creative[]): string[] {
  // Pick up to 4 unique suggestions across different types
  const seen = new Set<string>()
  const prompts: string[] = []

  for (const s of suggestions) {
    if (seen.has(s.type) || prompts.length >= 4) continue
    seen.add(s.type)
    const meta = creativeTypeMeta[s.type]
    if (!meta) continue
    // Keep the label's casing (platform names are proper nouns: TikTok, LinkedIn).
    const article = /^[aeiou]/i.test(meta.label) ? 'an' : 'a'
    prompts.push(`Create ${article} ${meta.label}: ${s.name}`)
  }

  return prompts.length > 0 ? prompts : FALLBACK_PROMPTS
}

/**
 * Room-specific openers: the empty state reacts to WHERE Taka was summoned.
 * On billing you get billing, not a grid of platforms to post to. Rooms not
 * listed fall back to the generic line; home keeps the full creation layout.
 */
/** Calendar-day label for a message: history carries metadata.createdAt from the
 * server; live messages have none and read as "Today". */
function messageDayLabel(msg: unknown): string {
  const createdAt = (msg as { metadata?: { createdAt?: string } }).metadata?.createdAt
  if (!createdAt) return 'Today'
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return 'Today'
  const now = new Date()
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (dayDiff <= 0) return 'Today'
  if (dayDiff === 1) return 'Yesterday'
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

/**
 * The agent introduces itself in first person, then reacts to WHERE it was
 * summoned (one colleague, many rooms). Styled as Taka's first message, not
 * a feature brochure: no platform grids, no capability lists.
 */
const INTRO_LEAD =
  "Hey, I'm Taka. I make your posts, keep your week scheduled, and can answer anything about your account."

const ROOM_LINES: Record<string, string> = {
  home: 'What should we make? Tell me an idea, or just ask.',
  calendar: "This is your week. Ask what's going out, or tell me to move something.",
  posts: "Everything you've made lives here. Ask about drafts, or start a new post.",
  brand: "This is where I keep what I know about your brand. Tell me a rule and I'll remember it.",
  integrations: "Ask what's connected, or what each platform unlocks.",
  billing: 'Ask about your plan, or what credits get used for.',
  collaborators: 'Ask me anything: your posts, your calendar, your account.',
  profile: 'Ask me anything: your posts, your calendar, your account.',
}

function EmptyState({
  suggestions,
  starterPrompts,
  surface,
  onPrompt,
}: {
  suggestions: Creative[]
  starterPrompts?: string[]
  surface?: string
  onPrompt: (text: string, index: number) => void
}) {
  const quickPrompts = useMemo(
    () => (starterPrompts?.length ? starterPrompts : buildQuickPrompts(suggestions)),
    [starterPrompts, suggestions]
  )

  const roomLine = (surface && ROOM_LINES[surface]) || ROOM_LINES.home

  return (
    <div className="flex h-full flex-col px-1 py-2">
      {/* Taka's self-introduction, laid out like its first message */}
      <div className="flex items-start gap-2.5">
        <img
          src={takaIcon}
          alt=""
          className="mt-0.5 h-7 w-7 flex-shrink-0 rounded-[6px] shadow-[0_0_14px_var(--acid-soft)]"
        />
        <div className="min-w-0">
          <p className="text-[13.5px] leading-relaxed text-paper">{INTRO_LEAD}</p>
          <p className="mt-2 text-[13.5px] leading-relaxed text-paper-dim">{roomLine}</p>
        </div>
      </div>

      <div className="mt-auto">
        <p className="mono mb-2 text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          Try asking
        </p>
        <div className="space-y-1.5">
          {quickPrompts.map((prompt, index) => (
            <button
              key={prompt}
              onClick={() => onPrompt(prompt, index)}
              className="w-full rounded-[8px] border border-line bg-bg-2 px-3 py-2 text-left text-[12px] text-paper-dim transition-colors hover:border-line-bright hover:bg-bg-3 hover:text-paper"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Assistant Chat ──────────────────────────────────────────────────────

export function AssistantChat({
  showHeader = true,
  onClose,
  surface,
  starterPrompts,
}: {
  showHeader?: boolean
  onClose?: () => void
  /** App screen the chat is summoned from — sent as ambient context per request. */
  surface?: string
  /** Screen-specific "Try asking" chips; falls back to suggestion-derived prompts. */
  starterPrompts?: string[]
}) {
  const [initialMessages, setInitialMessages] = useState<UIMessage[] | undefined>(undefined)
  const [messagesLoaded, setMessagesLoaded] = useState(false)

  // Fetch persistent assistant chat history
  useEffect(() => {
    request(OpenAPIConfig, {
      method: 'GET',
      url: '/v1/creatives/assistant-chat',
    })
      .then((data: any) => {
        if (data?.messages?.length) {
          setInitialMessages(
            data.messages.map((m: any) => ({
              id: m.id,
              role: m.role,
              parts: m.parts,
              metadata: m.metadata,
            })) as UIMessage[]
          )
        }
        setMessagesLoaded(true)
      })
      .catch(() => setMessagesLoaded(true))
  }, [])

  if (!messagesLoaded) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-bg-2">
        <Loader2 size={20} className="animate-spin text-paper-dim" />
      </div>
    )
  }

  return (
    <AssistantChatInner
      initialMessages={initialMessages}
      showHeader={showHeader}
      onClose={onClose}
      surface={surface}
      starterPrompts={starterPrompts}
    />
  )
}

// ─── Inner Component ─────────────────────────────────────────────────────

function AssistantChatInner({
  initialMessages,
  showHeader = true,
  onClose,
  surface,
  starterPrompts,
}: {
  initialMessages?: UIMessage[]
  showHeader?: boolean
  onClose?: () => void
  surface?: string
  starterPrompts?: string[]
}) {
  const { track } = useAnalytics()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { gate } = useSubscriptionGate()
  const outOfCredits = useAppSelector(selectIsOutOfCredits)
  const ideas = useIdeaStore(s => s.ideas)
  const suggestions = useMemo(
    () => ideas.map(i => i.primaryCreative).filter((c): c is Creative => !!c),
    [ideas]
  )
  const messageSourceRef = useRef<'typed' | 'quick_prompt' | 'option_chip'>('typed')

  // The transport's body() is evaluated per request; read the surface through a
  // ref so navigating between docked screens can never send a stale surface
  // (and the transport identity stays stable for useChat).
  const surfaceRef = useRef(surface)
  surfaceRef.current = surface
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/v1/creative/chat`,
        headers: () => ({
          ...(OpenAPI.TOKEN ? { Authorization: `Bearer ${OpenAPI.TOKEN}` } : {}),
        }),
        body: () => ({ ...(surfaceRef.current ? { surface: surfaceRef.current } : {}) }),
      }),
    []
  )

  const { messages, sendMessage, addToolApprovalResponse, status } = useChat({
    id: 'assistant-chat',
    transport,
    generateId: ulid,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    ...(initialMessages?.length ? { messages: initialMessages } : {}),
  })

  const [input, setInput] = useState('')
  const [emptyResponseMsgId, setEmptyResponseMsgId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const isLoading = status === 'streaming' || status === 'submitted'

  // Tappable picks from the agent's last question ([[options: a | b | c]]).
  // Only while the question is still the live tail — a user reply supersedes it.
  const quickReplies = useMemo(() => {
    if (isLoading) return []
    const last = messages[messages.length - 1]
    if (!last || last.role !== 'assistant') return []
    return parseOptions(partsText((last as { parts?: Part[] }).parts))
  }, [messages, isLoading])

  // Track restored message IDs so we don't replay their tool results
  const initialMessageIds = useMemo(
    () => new Set(initialMessages?.map(m => m.id) || []),
    [initialMessages]
  )

  // Auto-scroll — throttled to avoid layout thrashing during streaming
  const hasScrolledRef = useRef(false)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    clearTimeout(scrollTimerRef.current)
    scrollTimerRef.current = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: hasScrolledRef.current ? 'smooth' : 'auto',
      })
      hasScrolledRef.current = true
    }, 80)
    return () => clearTimeout(scrollTimerRef.current)
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle tool results — set generation status or navigate
  const handleToolResult = useCallback(
    (part: Record<string, unknown>) => {
      const output = part.output as Record<string, unknown> | undefined
      if (!output?.type || !output?.success) return

      // Brand writes ripple to the Brand page (if mounted) so it reflects
      // the agent's edit live instead of waiting for a remount.
      if (BRAND_TOOL_TYPES.has(output.type as string)) {
        window.dispatchEvent(new CustomEvent(BRAND_TOOL_ACTIVITY_EVENT))
      }

      // Schedule writes ripple to the Calendar page and Home week floor the
      // same way — the user is often staring at the grid the move changed.
      if (SCHEDULE_TOOL_TYPES.has(output.type as string)) {
        window.dispatchEvent(new CustomEvent(SCHEDULE_TOOL_ACTIVITY_EVENT))
      }

      if (output.type === 'createSocialPost' && output.creativeId) {
        if (output.status === 'generating') {
          // Background generation is running — card will show progress
          useGenerationStatusStore.getState().setStatus(output.creativeId as string, {
            status: 'generating',
          })
        } else if (output.url) {
          // No background generation — navigate directly to the project URL
          navigate(output.url as string)
        }
      }
    },
    [navigate]
  )

  const handleRetry = useCallback(() => {
    setEmptyResponseMsgId(null)
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    if (!lastUserMsg) return
    const textPart = ((lastUserMsg as { parts: Record<string, unknown>[] }).parts || []).find(
      p => p.type === 'text'
    )
    const text = (textPart?.text as string) || ''
    if (text) sendMessage({ text })
  }, [messages, sendMessage])

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault()
      const trimmed = input.trim()
      if (!trimmed || isLoading) return

      if (!gate('send_message')) return

      // Chat and text are free. Never block a send on credit balance; media
      // insufficiency is surfaced conversationally by the assistant.
      setEmptyResponseMsgId(null)
      setInput('')
      if (inputRef.current) inputRef.current.style.height = 'auto'

      const userMessages = messages.filter(m => m.role === 'user')
      track(AnalyticsEvents.AssistantChat.ASSISTANT_CHAT_MESSAGE_SENT, {
        message_length: trimmed.length,
        is_first_message: userMessages.length === 0,
        source: messageSourceRef.current,
        platform: isMobile ? 'mobile' : 'desktop',
        conversation_message_count: messages.length,
      })
      messageSourceRef.current = 'typed'

      try {
        await sendMessage({ text: trimmed })
      } catch (err) {
        setInput(trimmed)
        console.error('[AssistantChat] handleSubmit error:', err)
      }
    },
    [input, isLoading, sendMessage, gate, track]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Detect empty responses — check ALL new assistant messages (not just the last)
  // to avoid false positives in multi-step tool-use flows where the final assistant
  // turn may only contain reasoning parts.
  const prevLoadingRef = useRef(isLoading)
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      const newAssistantMsgs = messages.filter(
        m => m.role === 'assistant' && !initialMessageIds.has(m.id)
      )
      const lastAssistantMsg = newAssistantMsgs[newAssistantMsgs.length - 1]

      if (lastAssistantMsg) {
        const allParts = newAssistantMsgs.flatMap(
          m => (m as { parts: Record<string, unknown>[] }).parts || []
        )
        const hasToolCalls = allParts.some(
          p => typeof p.type === 'string' && (p.type as string).startsWith('tool-')
        )
        const hasText = allParts.some(
          p => p.type === 'text' && typeof p.text === 'string' && (p.text as string).trim()
        )

        // Check if a creative was created
        const creativeCreationPart = allParts.find(p => {
          if (typeof p.type !== 'string' || !(p.type as string).startsWith('tool-')) return false
          const toolName = typeof p.toolName === 'string' ? p.toolName : (p.type as string).slice(5)
          if (toolName !== 'createSocialPost') return false
          const output = p.output as Record<string, unknown> | undefined
          return !!output?.success && !!output?.creativeId
        })
        const creativeOutput = creativeCreationPart?.output as Record<string, unknown> | undefined

        track(AnalyticsEvents.AssistantChat.ASSISTANT_CHAT_MESSAGE_RECEIVED, {
          has_tool_calls: hasToolCalls,
          has_creative_creation: !!creativeCreationPart,
          creative_id: creativeOutput?.creativeId ? String(creativeOutput.creativeId) : undefined,
          creative_type: creativeOutput?.platform ? String(creativeOutput.platform) : undefined,
        })

        if (!hasToolCalls && !hasText) {
          setEmptyResponseMsgId(lastAssistantMsg.id)

          const diag = buildEmptyResponseDiagnostics(
            messages,
            newAssistantMsgs,
            allParts,
            lastAssistantMsg
          )
          console.warn(
            `[EMPTY_RESPONSE] Assistant chat\n` +
              `  Messages: ${diag.totalMessageCount}, New assistant: ${diag.newAssistantMessageCount}\n` +
              `  Last parts: ${diag.lastPartTypes}, All parts: ${diag.allPartTypes}`
          )
          track(AnalyticsEvents.AssistantChat.ASSISTANT_CHAT_EMPTY_RESPONSE, {
            total_message_count: diag.totalMessageCount,
            new_assistant_message_count: diag.newAssistantMessageCount,
            last_message_part_types: diag.lastPartTypes,
            all_new_part_types: diag.allPartTypes,
          })
        }
      }
    }
    prevLoadingRef.current = isLoading
  }, [isLoading, messages, initialMessageIds, track])

  // Waiting for response indicator
  const lastMsg = messages[messages.length - 1]
  const waitingForResponse =
    isLoading &&
    (lastMsg?.role === 'user' ||
      (lastMsg?.role === 'assistant' && (!lastMsg.parts || lastMsg.parts.length === 0)))

  return (
    <div className="flex h-full flex-col bg-bg-2 text-paper">
      {/* Header */}
      {showHeader && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <img src={takaIcon} alt="" className="h-5 w-5 rounded-[4px]" />
            <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">Taka Agent</h2>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="-mr-1 rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !isLoading && (
          <EmptyState
            suggestions={suggestions}
            starterPrompts={starterPrompts}
            surface={surface}
            onPrompt={(text, index) => {
              track(AnalyticsEvents.AssistantChat.ASSISTANT_CHAT_QUICK_PROMPT_TAPPED, {
                prompt_index: index,
                prompt_text: text,
                platform: isMobile ? 'mobile' : 'desktop',
              })
              messageSourceRef.current = 'quick_prompt'
              setInput('')
              sendMessage({ text })
            }}
          />
        )}
        {messages.map((msg, msgIndex) => {
          const parts = (msg as { parts: Record<string, unknown>[] }).parts || []
          // Day divider: the one-thread scrollback gets quiet orientation marks
          // where the calendar day changes. Live messages carry no metadata and
          // read as "today"; a leading "Today" divider on a fresh thread is noise.
          const dayLabel = messageDayLabel(msg)
          const prevDayLabel = msgIndex > 0 ? messageDayLabel(messages[msgIndex - 1]) : null
          const showDivider = dayLabel !== prevDayLabel && !(msgIndex === 0 && dayLabel === 'Today')
          // Extract createSocialPost tool results to render as full-width cards
          const creativeCards =
            msg.role === 'assistant'
              ? parts.filter(p => {
                  if (typeof p.type !== 'string' || !(p.type as string).startsWith('tool-'))
                    return false
                  const toolName =
                    typeof p.toolName === 'string' ? p.toolName : (p.type as string).slice(5)
                  if (toolName !== 'createSocialPost') return false
                  if (p.state !== 'output-available' || !p.output) return false
                  const output = p.output as Record<string, unknown>
                  return !!output.success && !!output.creativeId
                })
              : []

          return (
            <div key={msg.id}>
              {showDivider && (
                <div className="my-3 flex items-center gap-2">
                  <div className="h-px flex-1 bg-line" />
                  <span className="text-[10px] tracking-[0.15em] text-paper-faint uppercase">
                    {dayLabel}
                  </span>
                  <div className="h-px flex-1 bg-line" />
                </div>
              )}
              <ChatMessageBubble
                // Approvals answer ONLY on the LAST message (the SDK mutates +
                // resubmits the last message; older cards render expired). Kept
                // answerable even when restored: closing/reopening the dock
                // must not kill a seconds-old pending approval — the server
                // only expires approvals on non-last messages.
                onApprovalResponse={
                  msgIndex === messages.length - 1 ? addToolApprovalResponse : undefined
                }
                message={msg as { id: string; role: string; parts: Record<string, unknown>[] }}
                onToolResult={initialMessageIds.has(msg.id) ? undefined : handleToolResult}
                hideToolIndicators
              />
              {creativeCards.map(part => {
                const output = part.output as Record<string, unknown>
                const platform = (output.platform as string) || 'instagram'
                return (
                  <CreativeGenerationCard
                    key={`card-${output.creativeId}`}
                    creativeId={output.creativeId as string}
                    type={platform}
                    name={(output.name as string) || 'Creative'}
                    initialStatus={
                      (output.status as 'generating' | 'created' | 'failed') || 'created'
                    }
                    projectId={output.projectId as string}
                    platform={platform}
                  />
                )
              })}
            </div>
          )
        })}
        {emptyResponseMsgId && !isLoading && (
          <div className="mb-3 flex justify-start">
            <div className="alert max-w-[85%]" data-tone="warning" role="status">
              <div className="mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span className="font-medium">Something went wrong</span>
              </div>
              <p className="mb-2 text-[12px] opacity-90">
                The AI didn't generate a response. This can happen occasionally.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 rounded-[6px] border border-line-bright px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-bg-3"
              >
                <RefreshCw size={12} />
                Try again
              </button>
            </div>
          </div>
        )}
        {waitingForResponse && <ThinkingBubble />}
        <div ref={messagesEndRef} />
      </div>

      {/* Option chips: the agent's question offered concrete picks — render
          them as one-tap buttons (articulation barrier: buttons beat typing). */}
      {quickReplies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {quickReplies.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => {
                messageSourceRef.current = 'option_chip'
                sendMessage({ text: option })
              }}
              className="rounded-full border border-line bg-bg-2 px-3 py-1.5 text-[12px] text-paper-dim transition-colors hover:border-line-bright hover:bg-bg-3 hover:text-paper"
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-line p-3">
        {outOfCredits ? (
          <ComposerCreditBlock />
        ) : (
          <ComposerRow
            onSubmit={handleSubmit}
            trailing={
              <ComposerSendButton loading={isLoading} disabled={!input.trim() || isLoading} />
            }
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => {
                setInput(e.target.value)
                const el = e.target
                el.style.height = 'auto'
                const lineHeight = 20
                el.style.height = `${Math.min(el.scrollHeight, lineHeight * 6)}px`
              }}
              onKeyDown={handleKeyDown}
              placeholder="Ask Taka anything"
              rows={1}
              className="w-full resize-none bg-transparent py-2.5 !text-[13.5px] leading-5 font-normal text-paper outline-none placeholder:text-paper-faint"
            />
          </ComposerRow>
        )}
      </div>
    </div>
  )
}
